import { observer } from "owa-mobx-react";
import { useComputedValue } from "owa-react-hooks/lib/useComputed";
import React from "react";
import getPlaceholderAndAriaLabel from "../selectors/getPlaceholderAndAriaLabel";
import type KeyboardCharCodes from "owa-hotkeys/lib/utils/keyboardCharCodes";
import { getScenarioStore, SearchScenarioId } from "owa-search-store";
import { SEARCHBOX_INPUT_ELEMENT_ID, SUGGESTION_ID_PREFIX, SUGGESTIONS_CALLOUT_ID, FILTERS_BUTTON_ID, } from "owa-search-constants";
import { getSuggestions, lazyOnBackspacePressedSearchInput, lazyOnDownArrowPressedSearchInput, lazyOnLeftArrowPressedSearchInput, lazyOnUpArrowPressedSearchInput, lazyOnRightArrowPressedSearchInput, onEnterPressedSearchInput, onEscapePressedSearchInput, onKeyDownSearchInput, onSearchTextChanged, onSearchInputFocused, setIsSuggestionsDropdownVisible, setSearchTextForSuggestion, setInKeyboardSelectionMode, resetFocusFromSuggestionsToSearchInput, } from "owa-search-actions";

import classNames from "owa-classnames";
import { searchBoxInput, isDisabled as styles_isDisabled, inSearchInputPlaceholder, inputPlaceholder, searchBoxInputContainer, win32SearchBoxInput, } from "./SearchInput.scss";
import { shouldUseAppHostSearch } from "owa-app-host-search/lib/utils/shouldUseAppHostSearch";
import { getSelectedApp } from "owa-appbar-state";
import { ExternalAppId } from "owa-m365-acquisitions/lib/data/M365AppId";
import hasSuggestionPills from "../selectors/hasSuggestionPills";
import { getSelectedQuickActionId } from "../selectors/getSelectedQuickActionId";
import { SuggestionKind } from "owa-search-service/lib/data/schema/SuggestionSet";
import { safeRequestAnimationFrame } from "owa-performance";

export interface SearchInputProps {
    searchPlaceHolderText?: string;
    isDisabled?: boolean; // Boolean indicating if search box is disabled (default is false)
    scenarioId: SearchScenarioId;
    isSuggestionsCalloutVisible: boolean;
    ariaLabel?: string;
    useWin32Styling: boolean;
}

export interface SearchInputHandle {
    setFocus(): void;
}

const defaultExport: React.MemoExoticComponent<React.ForwardRefExoticComponent<Omit<SearchInputProps & React.RefAttributes<SearchInputHandle>, "ref"> & React.RefAttributes<SearchInputHandle>>> = observer(React.forwardRef(function SearchInput(props: SearchInputProps, ref: React.Ref<SearchInputHandle>) {
    const searchInput = React.useRef<HTMLInputElement>();
    const { searchPlaceHolderText, isDisabled, scenarioId, isSuggestionsCalloutVisible, ariaLabel, useWin32Styling, } = props;
    
    const assignSearchInputRef = React.useCallback((searchInputRef: HTMLInputElement) => {
        searchInput.current = searchInputRef;
    }, [searchInput]);
    
    // The text to be displayed in the input element.
    const searchInputText = useComputedValue((): string => getScenarioStore(scenarioId).searchText, [scenarioId]);
    const keyboardSelectedSuggestionIndex = useComputedValue((): number => getScenarioStore(scenarioId).selectedSuggestionIndex, [scenarioId]);
    const isInKeyboardSelectionMode = useComputedValue((): boolean => getScenarioStore(scenarioId).inKeyboardSelectionMode, [scenarioId]);
    
    React.useEffect(() => {
        if (!isInKeyboardSelectionMode) {
            return;
        }
        
        const selectedSuggestion = getScenarioStore(scenarioId).currentSuggestions?.Suggestions[keyboardSelectedSuggestionIndex];
        if (selectedSuggestion?.kind !== SuggestionKind.PeopleKeyword) {
            return;
        }
        
        // Adding the preview pill can reposition the controlled input. Wait until React
        // and the browser finish the arrow-key update before restoring the caret.
        const animationFrame = safeRequestAnimationFrame(() => {
            if (searchInput.current) {
                const cursorPosition = searchInput.current.value.length;
                searchInput.current.setSelectionRange(cursorPosition, cursorPosition);
            }
        });
        
        return () => {
            if (animationFrame !== undefined) {
                window.cancelAnimationFrame(animationFrame);
            }
        };
    }, [
        scenarioId,
        searchInputText,
        keyboardSelectedSuggestionIndex,
        isInKeyboardSelectionMode,
    ]);
    
    const activeDescendant = useComputedValue((): string => {
        const { selectedSuggestionIndex, selectedSuggestionQuickActionSelected, selectedSuggestionActiveQuickActionIndex, } = getScenarioStore(scenarioId);
        /**
         * If search suggestions are visible and the user is focused
         * on a quick action, then return the ID for the quick action
         * to be passed down for the aria-activedescendant attribute.
         * Otherwise, if search suggestions are visible and the user has a suggestion
         * selected, return the ID for the suggestion.
         */
        if (isSuggestionsCalloutVisible &&
            selectedSuggestionQuickActionSelected &&
            selectedSuggestionActiveQuickActionIndex >= 0) {
            return `${getSelectedQuickActionId(scenarioId)}`;
        }
        else if (isSuggestionsCalloutVisible && selectedSuggestionIndex >= 0) {
            return `${SUGGESTION_ID_PREFIX}${selectedSuggestionIndex}`;
        }
        return "";
    }, [isSuggestionsCalloutVisible, scenarioId]);
    
    /**
     * Sets focus on the search input. This is exposed publicly so parent component
     * (SearchBox) can set focus on the input element as a result of clicks on other
     * parts of the search box.
     */
    React.useImperativeHandle(ref, () => ({
        setFocus() {
            if (searchInput.current) {
                searchInput.current.focus();
            }
        },
    }), []);
    
    /**
     * Key handler for special key input into the input element.
     */
    const onKeyDown = React.useCallback(async (evt: React.KeyboardEvent<unknown>) => {
        const evtKeyCode = evt.keyCode;
        
        if (shouldStopPropagation(evt)) {
            evt.stopPropagation();
        }
        
        const { selectedSuggestionIndex, inKeyboardSelectionMode } = getScenarioStore(scenarioId);
        
        // Dispatch action to modify store based on key down.
        onKeyDownSearchInput(evtKeyCode, scenarioId);
        
        switch (evtKeyCode) {
            case 13: {
                // Prevent repeat queries if the Enter key is held down
                if (!evt.repeat) {
                    clearSelection(); // Clear selection (non-default behavior of IE/Edge).
                    evt.preventDefault();
                    onEnterPressedSearchInput(selectedSuggestionIndex, scenarioId);
                }
                break;
            }
            case 8: {
                const onBackspacePressedSearchInput = await lazyOnBackspacePressedSearchInput.import();
                onBackspacePressedSearchInput(getCursorPosition(), scenarioId);
                break;
            }
            case 38:
                // Prevents cursor from going to beginning of input.
                evt.preventDefault();
                const onUpArrowPressedSearchInput = await lazyOnUpArrowPressedSearchInput.import();
                onUpArrowPressedSearchInput(scenarioId);
                break;
            case 40:
                const onDownArrowPressedSearchInput = await lazyOnDownArrowPressedSearchInput.import();
                onDownArrowPressedSearchInput(scenarioId);
                break;
            case 37: {
                const onLeftArrowPressedSearchInput = await lazyOnLeftArrowPressedSearchInput.import();
                onLeftArrowPressedSearchInput(getCursorPosition(), scenarioId, evt);
                break;
            }
            case 27: {
                // Prevents IE/Edge behavior of putting last text back into the input.
                evt.preventDefault();
                onEscapePressedSearchInput(scenarioId);
                break;
            }
            case 39: {
                const onRightArrowPressedSearchInput = await lazyOnRightArrowPressedSearchInput.import();
                onRightArrowPressedSearchInput(scenarioId, evt);
                break;
            }
            default: {
                // Special case when in the OrgExplorer app.  Should not allow the user to type in the search box if there are suggestion pills
                if (shouldUseAppHostSearch() &&
                    getSelectedApp() === ExternalAppId.OrgExplorer &&
                    hasSuggestionPills(scenarioId)) {
                    evt.preventDefault();
                }
                
                // Special case when in People app. User should only select one suggestion pill at a time.
                // Should not allow the user to type in the search box if there is suggestion pill.
                // But use tab key to navigate to the search button or tab + shift to navigate to the back button.
                if (scenarioId === SearchScenarioId.People &&
                    hasSuggestionPills(scenarioId) &&
                    evtKeyCode !== 9 &&
                    !evt.shiftKey) {
                    evt.preventDefault();
                }
                
                if (inKeyboardSelectionMode) {
                    setInKeyboardSelectionMode(scenarioId, false);
                }
                break;
            }
        }
    }, [scenarioId]);
    
    /**
     * Click handler when user clicks in input.
     */
    const onClick = React.useCallback((evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        setIsSuggestionsDropdownVisible(true, scenarioId);
        resetFocusFromSuggestionsToSearchInput(scenarioId);
    }, [scenarioId]);
    
    /**
     * Focus handler for when the SearchInput component receives focus.
     *
     * We want to swallow the focus event if we are coming from
     * a suggestion (child component) within the SearchInput container.
     */
    const onFocus = React.useCallback((e: React.FocusEvent<EventTarget>) => {
        let relatedTargetElement = e.relatedTarget as HTMLElement;
        
        // Fallback for IE, which doesn't always set relatedTarget.
        if (!relatedTargetElement) {
            relatedTargetElement = document.activeElement as HTMLElement;
        }
        
        const isTargetElementWithinSearchBox = relatedTargetElement.id.indexOf(SUGGESTION_ID_PREFIX) > -1;
        
        // Check if the previous element focused was the Advanced Search icon
        let isFocusAfterClickingWhileHover = relatedTargetElement.id === FILTERS_BUTTON_ID;
        
        // For V9 flight: also check if focus came from within V9 advanced search container
        if (!isFocusAfterClickingWhileHover) {
            isFocusAfterClickingWhileHover =
                relatedTargetElement.closest("[data-advanced-search]") !== null;
        }
        
        // Truly "focus" only if event is from outside of search box.
        // And the Focus event was not triggered by a hover state
        if (!isTargetElementWithinSearchBox && !isFocusAfterClickingWhileHover) {
            setIsSuggestionsDropdownVisible(true, scenarioId);
            onSearchInputFocused(scenarioId);
        }
    }, [scenarioId]);
    
    const { searchSessionGuid, isSuggestionsDropdownVisible } = getScenarioStore(scenarioId);
    /**
     * Need to do this synchronously or there'll be rendering issues and the value
     * of the input element will be empty.
     */
    const onSearchTextChange = React.useCallback(() => {
        const searchText = searchInput?.current?.value ?? "";
        onSearchTextChanged(searchText, scenarioId);
        setSearchTextForSuggestion(searchText, scenarioId);
        getSuggestions(scenarioId);
        // If suggestions dropdown is hidden, show it.
        if (!isSuggestionsDropdownVisible) {
            setIsSuggestionsDropdownVisible(true, scenarioId);
        }
    }, [scenarioId, searchInput.current?.value, isSuggestionsDropdownVisible]);
    
    /**
     * Gets cursor position if no text is selected. If text is selected, then
     * -1 is returned.
     */
    const getCursorPosition = (): number => {
        /**
         * If reference to input is null (component unmounted), return -1
         * indicating that cursor position shouldn't be used.
         */
        if (!searchInput.current ||
            searchInput.current.selectionStart === undefined ||
            searchInput.current.selectionStart === null) {
            return -1;
        }
        const isTextSelected = searchInput.current.selectionStart !== searchInput.current.selectionEnd;
        return isTextSelected ? -1 : searchInput.current.selectionStart;
    };
    /**
     * Clears selected text in the input.
     */
    const clearSelection = () => {
        const searchInputEnd = searchInput.current?.value?.length ?? 0;
        searchInput.current?.setSelectionRange(searchInputEnd, searchInputEnd);
    };
    
    const isSearchBoxExpanded = !!searchSessionGuid;
    const searchInputClasses = classNames(searchBoxInput, isDisabled && styles_isDisabled, isSearchBoxExpanded ? inSearchInputPlaceholder : inputPlaceholder, useWin32Styling && win32SearchBoxInput);
    const { ariaLabel: ariaLabelFromGetter, placeholder } = getPlaceholderAndAriaLabel(scenarioId, searchPlaceHolderText ?? "");
    
    return (<div className={searchBoxInputContainer}>
                <input role="combobox" aria-activedescendant={activeDescendant} aria-autocomplete="list" aria-controls={isSuggestionsCalloutVisible ? SUGGESTIONS_CALLOUT_ID : undefined} aria-expanded={isSuggestionsCalloutVisible} aria-label={ariaLabel ?? ariaLabelFromGetter} autoComplete="off" className={searchInputClasses} disabled={isDisabled} onChange={onSearchTextChange} onClick={onClick} onFocus={onFocus} onKeyDown={onKeyDown} placeholder={placeholder} ref={assignSearchInputRef} value={searchInputText} spellCheck={false} autoCorrect="off" id={SEARCHBOX_INPUT_ELEMENT_ID}/>
            </div>);
}), "SearchInput");
export default defaultExport;

function shouldStopPropagation(evt: React.KeyboardEvent<unknown>) {
    // Allow Alt combinations to propagate so that useCustomKeytipEvents can suppress keytip
    // activation when Alt is released. This includes modifier-only Alt+Shift and Shift+Alt.
    const isAltKeyCombination = evt.altKey && (evt.key !== "Alt" || evt.shiftKey || evt.ctrlKey);
    
    return !(isAltKeyCombination ||
        evt.ctrlKey ||
        (evt.ctrlKey && evt.shiftKey) ||
        evt.keyCode === 117);
}
